/* eslint-disable react/jsx-props-no-spreading */
import AddIcon from '@mui/icons-material/Add';
import { Button, Checkbox, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import Select from '../components/forms/Select';
import ReportTemplateSection from '../components/ReportingHub/ReportTemplateSection';
import { API_USERS_TRAFFICKER_LIST_URL } from '../constants/apiUrls';
import { REPORTING_HUB_NEW_URL } from '../constants/routeUrls';
import { useConfig } from '../hooks/useConfig';
import { useLocalStorage } from '../hooks/useLocalStorage';
import useReportTemplates from '../hooks/useReportTemplates';
import { isAchHost } from '../util/achAuth';
import { unwrapAchBareCollection } from '../util/achEnvelope';
import posthog from '../util/posthog';
import { getRequestJSON } from '../util/services';
import { ReportingHubPageContext } from './ReportingHubPageContext';

function ReportingHubPage() {
  const { apiHost, apiHostFor } = useConfig();

  // The traffickers role list opts into the per-domain flip (ACH-colk); ACH
  // wraps the Rails bare array in the SPEC §2 {collection} envelope — unwrap.
  const usersHost = apiHostFor('users');

  const [user] = useLocalStorage('user', null);
  const navigate = useNavigate();
  const { id, name } = user;
  const form = useForm({
    defaultValues: {
      userIds: [id],
    },
  });

  const { watch } = form;

  const userIds = watch('userIds');

  const { data, isLoading, refetch } = useReportTemplates(apiHost, userIds);

  useEffect(() => {
    if (!isLoading && data?.collection) {
      posthog.capture('reporting_hub_viewed', { template_count: data.collection.length });
    }
  }, [isLoading, data]);

  const { data: userList } = useQuery({
    queryKey: ['traffickers'],
    queryFn: () =>
      getRequestJSON(usersHost, API_USERS_TRAFFICKER_LIST_URL).then((res) =>
        unwrapAchBareCollection(res, isAchHost(usersHost))
      ),
  });

  if (userList && !userList.find((userItem) => userItem.id === id)) {
    userList.splice(0, 0, {
      id,
      name,
    });
  }

  const traffickersList =
    (userList &&
      userList.map((userItem) => ({
        value: userItem.id,
        label: userItem.name,
      }))) ||
    [];

  const subtitle4 = {
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '28px',
  };

  const firstPartyDate = data?.meta?.latest1PDate;
  const thirdPartyDate = data?.meta?.latest3PDate;
  const reportTemplates = data?.collection;

  const value = useMemo(
    () => ({
      refetch,
      isLoading,
      reportTemplates,
      firstPartyDate,
      thirdPartyDate,
    }),
    [refetch, isLoading, reportTemplates, firstPartyDate, thirdPartyDate]
  );

  const customLabel = (option, selectedValues) => (
    <Stack direction="row" spacing={2} py={1}>
      <Checkbox checked={selectedValues.includes(option.value)} sx={{ p: 0, pr: 2 }} />
      {option.label}
    </Stack>
  );

  return (
    <ReportingHubPageContext.Provider value={value}>
      <FormProvider {...form}>
        <Stack p={2} spacing={2}>
          <Stack direction="row" justifyContent="space-between">
            <Stack spacing={-1}>
              <Typography sx={subtitle4}>
                Generate reports for clients and manage report templates from the latest data available
              </Typography>
              <Stack direction="row" spacing={1}>
                <Typography sx={subtitle4}>First Party: {firstPartyDate}</Typography>
                <Typography sx={subtitle4}>Third Party: {thirdPartyDate}</Typography>
              </Stack>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Stack width="200px">
                <Select
                  label=""
                  name="userIds"
                  size="small"
                  options={traffickersList}
                  multiple
                  customLabel={customLabel}
                  renderValue={(selectedIdArray) =>
                    selectedIdArray
                      .map((selectedId) => traffickersList.find((trafficker) => trafficker.value === selectedId).label)
                      .join(', ')
                  }
                />
              </Stack>
              <Stack>
                <Button onClick={() => navigate(REPORTING_HUB_NEW_URL)} startIcon={<AddIcon />}>
                  Create New Template
                </Button>
              </Stack>
            </Stack>
          </Stack>
          <ReportTemplateSection name={name} userList={userList || []} />
        </Stack>
      </FormProvider>
    </ReportingHubPageContext.Provider>
  );
}

export default ReportingHubPage;
